import type { FilterColumn } from './FilterColumn';
import type { SortColumn } from './SortColumn';
import type { GridEventHandler } from './GridEventHandler';
import type { GridStatePersistenceContract } from './GridStatePersistenceContract';
import type { OriginalGridHandlers } from './OriginalGridHandlers';
import type { PersistedGridState } from './PersistedGridState';

/**
 * An opt-in decorator that wraps a React grid component's filter, sort, and
 * paging handlers with:
 *
 * 1. Structured `console.log` tracing.
 * 2. Automatic persistence of the current grid state to `localStorage`
 *    under a caller-supplied key.
 *
 * All three state slices (filters, sorts, paging) are stored together as a
 * single JSON value so the key space stays clean.
 *
 * @example
 * ```ts
 * const persistence = new GridStatePersistence('employee-grid', {
 *   onFilter: existingFilterFn,
 * });
 *
 * // On every filter / sort / page change the state is saved automatically.
 * persistence.onFilter(filters, sorts, 1, 25);
 *
 * // On page reload, read the saved state back:
 * const saved = GridStatePersistence.load('employee-grid');
 * ```
 */
export class GridStatePersistence implements GridStatePersistenceContract {
  private readonly originalHandlers: OriginalGridHandlers;
  private readonly storageName: string;

  /**
   * When `true`, diagnostic messages are logged to the browser console
   * for every persist event.  Defaults to `false`.
   */
  public debug: boolean;

  constructor(storageName: string, originalHandlers: OriginalGridHandlers = {}, debug = false) {
    this.storageName = storageName;
    this.originalHandlers = originalHandlers;
    this.debug = debug;
  }

  // ── Static helpers ───────────────────────────────────────────────────

  /**
   * Load a previously-persisted grid state from `localStorage`.
   * Returns `null` when no entry exists or the value cannot be parsed.
   */
  public static load(storageName: string): PersistedGridState | null {
    if (typeof window === 'undefined') return null;

    try {
      const raw = localStorage.getItem(storageName);
      if (!raw) return null;
      return JSON.parse(raw) as PersistedGridState;
    } catch {
      return null;
    }
  }

  /**
   * Remove the persisted grid state from `localStorage`.
   */
  public static clear(storageName: string): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(storageName);
    } catch {
      // localStorage may be disabled — swallow silently.
    }
  }

  // ── Public instance methods ───────────────────────────────────────────

  /**
   * Remove the persisted grid state for this instance's storage key.
   */
  public clearPreference = (): void => {
    GridStatePersistence.clear(this.storageName);
  };

  // ── Public decorated handlers ────────────────────────────────────────

  /**
   * Decorated filter handler.
   * Persists the full grid state, logs the call, then delegates to the
   * original `onFilter` (if provided).
   */
  public onFilter: GridEventHandler = (
    filterColumns,
    sortColumns,
    pageSize,
    hiddenColumns,
    columnOrder,
  ): void => {
    this.persistState(filterColumns, sortColumns, pageSize, hiddenColumns, columnOrder);
    this.logEvent('onFilter', filterColumns, sortColumns, pageSize, hiddenColumns, columnOrder);
    this.originalHandlers.onFilter?.(filterColumns, sortColumns, pageSize, hiddenColumns, columnOrder);
  };

  /**
   * Decorated sort handler.
   */
  public onSort: GridEventHandler = (
    filterColumns,
    sortColumns,
    pageSize,
    hiddenColumns,
    columnOrder,
  ): void => {
    this.persistState(filterColumns, sortColumns, pageSize, hiddenColumns, columnOrder);
    this.logEvent('onSort', filterColumns, sortColumns, pageSize, hiddenColumns, columnOrder);
    this.originalHandlers.onSort?.(filterColumns, sortColumns, pageSize, hiddenColumns, columnOrder);
  };

  /**
   * Decorated paging handler.
   */
  public onPaging: GridEventHandler = (
    filterColumns,
    sortColumns,
    pageSize,
    hiddenColumns,
    columnOrder,
  ): void => {
    this.persistState(filterColumns, sortColumns, pageSize, hiddenColumns, columnOrder);
    this.logEvent('onPaging', filterColumns, sortColumns, pageSize, hiddenColumns, columnOrder);
    this.originalHandlers.onPaging?.(filterColumns, sortColumns, pageSize, hiddenColumns, columnOrder);
  };

  // ── Private helpers ──────────────────────────────────────────────────

  private persistState(
    filterColumns: FilterColumn[],
    sortColumns: SortColumn[],
    pageSize: number,
    hiddenColumns: string[],
    columnOrder: string[],
  ): void {
    if (typeof window === 'undefined') return;

    const state: PersistedGridState = {
      filterColumns,
      sortColumns,      
      pageSize,
      hiddenColumns,
      columnOrder,
    };

    try {
      localStorage.setItem(this.storageName, JSON.stringify(state));
    } catch {
      // localStorage may be full or disabled — swallow silently.
    }
  }

  private logEvent(
    methodName: string,
    filterColumns: FilterColumn[],
    sortColumns: SortColumn[],
    pageSize: number,
    hiddenColumns: string[],
    columnOrder: string[],
  ): void {
    if (!this.debug) return;

    console.log(
      `[GridStatePersistence.${methodName}]`,
      {
        storageName: this.storageName,
        filterColumns,
        sortColumns,
        pageSize,
        hiddenColumns,
        columnOrder,
      },
    );
  }
}
